import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { accessibilityEnhanceHistory, useAccessibilityPageTitle, accessibilitySetPageLang } from '@skillz/accessibility';
import { initTexts } from '../../store/translate-actions';
import { deviceTypes } from '../../enums/deviceTypes';
import { translateActions } from '../../store/translate-slice';
import { getGamification } from '../../store/gamification-action';
import { getUserAllSchoolProperty, isKosherUser, elementaryStudentOrTeacher } from '../../utils/utils';
import { getUserDetails } from '../../store/auth-actions';
import { initAlbertAssessments } from '../../store/assessments-actions';
import useLevels from '../../hooks/useLevelsBar';
import LogoutPage from '../layout/logoutPage/LogoutPage';
import Layout from '../layout/Layout';
import Home from '../pages/home/Home';
import useGamification from '../../hooks/useGamification';
import LayoutMobile from '../layout/layoutMobile/LayoutMobile';
import pathRouts from '../../enums/pathRoute';
import ChatAi from '../shared/chatAi/ChatAi';
import Curatorship from '../pages/curatorship/Curatorship';
import Albert from '../shared/albert/Albert';
import DelayNavigatePage from '../pages/delayNavigatePage/DelayNavigatePage';
import useAnalytics from '../../hooks/useAnalytics';
import titleMap from './titleMap';
import '@skillz/accessibility/dist/index.css';
import './App.css';
import useLmsAction from '../../hooks/useLmsAction';
import objectsNameTypes from '../../enums/objectsNameTypes';

function App() {

  const levels = useLevels();
  const deviceType = useSelector(state => state.responsive.deviceType);
  const { providers } = useSelector((state) => state.gamification);
  const { texts, lang } = useSelector(state => state.translate);
  const { isAuth, userDetails, isStudent, user } = useSelector(state => state.auth);
  const { methodica } = useSelector((state) => state.assessments);
  const [hasAccess, setHasAccess] = useState()
  const [hasQLevels, setHasQLevels] = useState(false)
  const [searchParams] = useSearchParams();

  const dispatch = useDispatch()
  useAccessibilityPageTitle(texts.pageTitle, titleMap, lang);
  useAnalytics();
  useGamification();
  useLmsAction(`${window.location.origin}/ai`, objectsNameTypes.app, true)

  useEffect(() => {
    accessibilityEnhanceHistory();
  }, [])

  useEffect(() => {
    if (lang) {
      accessibilitySetPageLang(lang);
    }
  }, [lang]);

  useEffect(() => {
    if (isAuth)
      dispatch(getUserDetails());
  }, [dispatch, isAuth])

  useEffect(() => {
    if (isAuth !== null && lang) {
      if (isAuth === true)
        userDetails && dispatch(initTexts())
      else
        dispatch(initTexts());
    }
  }, [dispatch, userDetails, isAuth, lang])

  useEffect(() => {
    if (isAuth && !providers)
      dispatch(getGamification())
    // eslint-disable-next-line
  }, [dispatch, isAuth])

  useEffect(() => {
    if (isAuth !== null) {
      if (!user || (user && userDetails && methodica))
        setHasQLevels(true)
    }
  }, [methodica, user, userDetails, isAuth])

  useEffect(() => {
    if (isAuth !== null && !methodica)
      dispatch(initAlbertAssessments())
  }, [methodica, dispatch, isAuth])

  useEffect(() => {
    if (isAuth !== null) {
      const currentLnag = searchParams.get("lang");
      dispatch(translateActions.initLang({ currentLnag: currentLnag, user: user, userDetails: userDetails }));
    }
  }, [dispatch, isAuth, user, userDetails]);

  const defaultNavigate = () => {
    const { defaultRoute } = window.config;
    if (!isAuth)
      return pathRouts.HOME.route;
    
    const isElementary = elementaryStudentOrTeacher(user, userDetails, isStudent);
    
    if (isKosherUser(userDetails)) {
      if (isStudent) {
        if (isElementary && defaultRoute.studentKosherElementary)
          return deviceType !== deviceTypes.mobile ? defaultRoute.studentKosherElementary.desktop : defaultRoute.studentKosherElementary.mobile;
        return deviceType !== deviceTypes.mobile ? defaultRoute.studentKosher.desktop : defaultRoute.studentKosher.mobile;
      }
      if (isElementary && defaultRoute.teacherKosherElementary)
        return deviceType !== deviceTypes.mobile ? defaultRoute.teacherKosherElementary.desktop : defaultRoute.teacherKosherElementary.mobile;
      return deviceType !== deviceTypes.mobile ? defaultRoute.teacherKosher.desktop : defaultRoute.teacherKosher.mobile;
    }
    if (isStudent) {
      if (isElementary && defaultRoute.studentElementary)
        return deviceType !== deviceTypes.mobile ? defaultRoute.studentElementary.desktop : defaultRoute.studentElementary.mobile;
      if (levels?.isChallengeCompleted())
        return deviceType !== deviceTypes.mobile ? defaultRoute.student.finishedQLevel.desktop : defaultRoute.student.finishedQLevel.mobile;
      return deviceType !== deviceTypes.mobile ? defaultRoute.student.desktop : defaultRoute.student.mobile;
    }
    if (isElementary && defaultRoute.teacherElementary)
      return deviceType !== deviceTypes.mobile ? defaultRoute.teacherElementary.desktop : defaultRoute.teacherElementary.mobile;
    return deviceType !== deviceTypes.mobile ? defaultRoute.teacher.desktop : defaultRoute.teacher.mobile;
  };

  useEffect(() => {
    let access = true;
    const whiteList = window.config.whiteList || [];
    let accessFromWhiteList;
    if (isAuth !== null) {
      if (isAuth) {
        if (userDetails) {
          if (whiteList.length > 0) {
            const userInWhiteList = whiteList.includes(user?.exidentifier);
            const userSchools = getUserAllSchoolProperty(userDetails, 'id') || [];
            const userSchoolInWhiteList = whiteList.some(item => userSchools.map(s => `${s}`).includes(item));
            if ((userInWhiteList || userSchoolInWhiteList))
              accessFromWhiteList = true
            else
              access = false;
          }
          if (window.config.onlyTeacherAccess && !accessFromWhiteList) {
            if (isStudent)
              access = false;
            else
              access = true;
          }

          if (isStudent && window.config.lockedAppClass?.includes(Number(user?.studentkita)))
            access = false

          if (isStudent && window.config.lockedCharediStudents && isKosherUser(userDetails))
            access = false

          if (access === false) {
            window.location.href = `${window.location.origin}`;
          }
        }
      }
      setHasAccess(access);
    }

  }, [isAuth, isStudent, user, userDetails])


  return (
    (() =>
      <Routes>
        {hasAccess && hasQLevels && (
          <Route element={deviceType !== deviceTypes.mobile ? <Layout /> : <LayoutMobile />}>
            <Route path="*" element={<Navigate to={defaultNavigate()} />} />

            {!isAuth ?
              <Route path={pathRouts.HOME.route} element={<LogoutPage />} />
              :
              <Route>
                <Route path={pathRouts.HOME.route} element={deviceType !== deviceTypes.mobile ? <Navigate to={defaultNavigate()} /> : <Home />} />
                <Route path={pathRouts.CURATORSHIP.route} element={<Curatorship />} />
                {(!isStudent || !isKosherUser(userDetails)) && <Route path={pathRouts.CHATALBERT.route} element={<Albert />} />}
                {/* {(!isStudent || !isKosherUser(userDetails)) && <Route path={pathRouts.CHATAI.route} element={<ChatAi />} />} */}
              </Route>
            }

          </Route>
        )}
        <Route path={`${pathRouts.DELAYNAVIGATE.route}/:link`} element={<DelayNavigatePage />} />
      </Routes>
    )()
  );
}

export default App;
